import { ROOT_2LN2, GAUSSIAN_EXP_FACTOR, ROOT_PI_OVER_LN2, } from '../../../util/constants';
import erfinv from '../../../util/erfinv';
import { Shape1D } from '../Shape1D';
export class Gaussian extends Shape1D {
    constructor(options = {}) {
        super();
        const { fwhm = 500, sd, height } = options;
        this.fwhm = sd ? widthToFWHM(2 * sd) : fwhm;
        this.height =
            height === undefined
                ? Math.sqrt(-GAUSSIAN_EXP_FACTOR / Math.PI) / this.fwhm
                : height;
    }
    fwhmToWidth(fwhm = this.fwhm) {
        return fwhmToWidth(fwhm);
    }
    widthToFWHM(width) {
        return widthToFWHM(width);
    }
    fct(x) {
        return fct(x, this.fwhm);
    }
    getArea() {
        return getArea({ fwhm: this.fwhm, height: this.height });
    }
    getFactor(area) {
        return getFactor(area);
    }
    getData(options = {}) {
        const { length, factor } = options;
        return getData({ fwhm: this.fwhm, height: this.height, factor, length });
    }
}
/**
 * Return a parameterized function of a gaussian shape (see README for equation).
 * @returns - the y value of gaussian with the current parameters.
 */
export function fct(x, fwhm) {
    return Math.exp(GAUSSIAN_EXP_FACTOR * Math.pow(x / fwhm, 2));
}
/**
 * Compute the value of Full Width at Half Maximum (FWHM) from the width between the inflection points.
 * for more information check the [mathworld page](https://mathworld.wolfram.com/GaussianFunction.html)
 * @returns fwhm
 */
export function widthToFWHM(width) {
    return width * ROOT_2LN2;
}
/**
 * Compute the value of width between the inflection points from Full Width at Half Maximum (FWHM).
 * for more information check the [mathworld page](https://mathworld.wolfram.com/GaussianFunction.html)
 * @param fwhm - Full Width at Half Maximum.
 * @returns width
 */
export function fwhmToWidth(fwhm) {
    return fwhm / ROOT_2LN2;
}
/**
 * Calculate the area of a specific shape.
 * @returns returns the area of the specific shape and parameters.
 */
export function getArea(options) {
    let { fwhm, sd, height = 1 } = options;
    if (sd)
        fwhm = widthToFWHM(2 * sd);
    if (fwhm === undefined) {
        throw new Error('should pass fwhm or sd parameters');
    }
    return (height * ROOT_PI_OVER_LN2 * fwhm) / 2;
}
/**
 * Calculate the number of times FWHM allows to reach a specific area coverage.
 * @param [area=0.9999] Expected area to be covered.
 * @returns
 */
export function getFactor(area = 0.9999) {
    return Math.sqrt(2) * erfinv(area);
}
/**
 * Calculate intensity array of a gaussian shape.
 * @returns {Float64Array} Intensity values.
 */
export function getData(options = {}) {
    let { length, factor = getFactor(), fwhm = 500, sd, height } = options;
    if (sd)
        fwhm = widthToFWHM(2 * sd);
    if (!height) {
        height = Math.sqrt(-GAUSSIAN_EXP_FACTOR / Math.PI) / fwhm;
    }
    if (!length) {
        length = Math.min(Math.ceil(fwhm * factor), Math.pow(2, 25) - 1);
        if (length % 2 === 0)
            length++;
    }
    const center = (length - 1) / 2;
    const data = new Float64Array(length);
    for (let i = 0; i <= center; i++) {
        data[i] = fct(i - center, fwhm) * height;
        data[length - 1 - i] = data[i];
    }
    return data;
}
//# sourceMappingURL=Gaussian.js.map